/**
 * The quick-edit studio's pure logic: how the creator's actions change the
 * edit list (trim at the playhead, cut a stretch out, add a logo or a line of
 * text, change speed), undo and redo, and the words the screen shows for it.
 * No React here, so it is tested as plain functions.
 */

import {
  type Anchor,
  type EditList,
  type TextEffect,
  type TextEntrance,
  type Overlay,
  type Position,
  type Segment,
  type TextOverlay,
  type TextStyle,
  type Voiceover,
  EDIT_LIMITS,
  emptyEditList,
  outputDurationMs,
  outputTimeAt,
  playedSegments,
  segmentPlacements,
  sourceTimeAt,
} from '../../utils/editing/editList';

export const MIN_SEGMENT_MS = EDIT_LIMITS.minSegmentMs;

let counter = 0;
/** Short unique ids for new items (the API only needs them unique within the list). */
export const newItemId = (prefix: string): string => {
  counter += 1;
  return `${prefix}-${Date.now().toString(36)}${counter.toString(36)}`;
};

/** The explicit segments of a list: the whole source when it had none. */
export const explicitSegments = (list: EditList): Segment[] =>
  playedSegments(list).map((segment) => (segment.id === 'all' ? { ...segment, id: newItemId('seg') } : segment));

/** Splits the stretch playing at an output time into two, at that moment. */
export function splitAt(list: EditList, outputMs: number): EditList {
  const placements = segmentPlacements(list);
  const hit = placements.find((p) => outputMs > p.startMs && outputMs < p.endMs);
  if (!hit) return list;
  const sourceMs = Math.round(hit.segment.inMs + (outputMs - hit.startMs) * hit.segment.speed);
  if (sourceMs - hit.segment.inMs < MIN_SEGMENT_MS || hit.segment.outMs - sourceMs < MIN_SEGMENT_MS) return list;
  const segments = explicitSegments(list);
  const index = placements.indexOf(hit);
  const first = { ...segments[index], outMs: sourceMs };
  const second = { ...segments[index], id: newItemId('seg'), inMs: sourceMs };
  return { ...list, segments: [...segments.slice(0, index), first, second, ...segments.slice(index + 1)] };
}

/** Removes a stretch of the result (output times): what was outside it closes up. */
export function removeRange(list: EditList, fromMs: number, toMs: number): EditList {
  if (toMs <= fromMs) return list;
  const from = Math.max(0, fromMs);
  const to = Math.min(outputDurationMs(list), toMs);
  const kept: Segment[] = [];
  segmentPlacements(list).forEach(({ segment, startMs, endMs }, index) => {
    const id = segment.id === 'all' ? `seg-${index}` : segment.id;
    const before = Math.min(endMs, from);
    const after = Math.max(startMs, to);
    if (before - startMs >= MIN_SEGMENT_MS * (1 / segment.speed) && before > startMs) {
      kept.push({ ...segment, id, outMs: Math.round(segment.inMs + (before - startMs) * segment.speed) });
    }
    if (endMs - after >= MIN_SEGMENT_MS * (1 / segment.speed) && after < endMs) {
      kept.push({ ...segment, id: before > startMs ? newItemId('seg') : id, inMs: Math.round(segment.inMs + (after - startMs) * segment.speed) });
    }
  });
  // Everything that was placed later moves up with the cut.
  const shift = (ms: number): number => (ms <= from ? ms : ms >= to ? ms - (to - from) : from);
  return shiftPlaced({ ...list, segments: kept.filter((s) => s.outMs - s.inMs >= MIN_SEGMENT_MS) }, shift);
}

/** Keeps only one stretch of the result (a Short): everything else goes. */
export function keepOnly(list: EditList, fromMs: number, toMs: number): EditList {
  const total = outputDurationMs(list);
  let next = list;
  if (toMs < total) next = removeRange(next, toMs, total);
  if (fromMs > 0) next = removeRange(next, 0, fromMs);
  return next;
}

/** Moves every placed item's times through `shift` and drops what lands at zero length. */
function shiftPlaced(list: EditList, shift: (ms: number) => number): EditList {
  const total = outputDurationMs(list);
  const clamp = (ms: number) => Math.max(0, Math.min(total, ms));
  const overlays = list.overlays
    .map((overlay) => ({ ...overlay, fromMs: overlay.fromMs === null ? null : clamp(shift(overlay.fromMs)), toMs: overlay.toMs === null ? null : clamp(shift(overlay.toMs)) }))
    .filter((overlay) => overlay.fromMs === null || overlay.toMs === null || overlay.toMs > overlay.fromMs);
  const texts = list.texts
    .map((text) => ({ ...text, fromMs: clamp(shift(text.fromMs)), toMs: clamp(shift(text.toMs)) }))
    .filter((text) => text.toMs > text.fromMs);
  const voiceovers = list.voiceovers.map((voiceover) => ({ ...voiceover, atMs: clamp(shift(voiceover.atMs)) }));
  const music = list.music ? { ...list.music, startMs: clamp(shift(list.music.startMs)) } : null;
  return { ...list, overlays, texts, voiceovers, music };
}

/**
 * After the result got shorter (a speed change, a tighter stretch): anything placed
 * past the new end slides back so it still shows, keeping its length where it can.
 */
function fitPlaced(list: EditList): EditList {
  const total = outputDurationMs(list);
  const fit = (fromMs: number, toMs: number): { fromMs: number; toMs: number } => {
    if (toMs <= total) return { fromMs, toMs };
    const length = toMs - fromMs;
    return { fromMs: Math.max(0, total - length), toMs: total };
  };
  const overlays = list.overlays.map((overlay) => {
    if (overlay.toMs === null) return { ...overlay, fromMs: overlay.fromMs === null ? null : Math.min(overlay.fromMs, Math.max(0, total - 1000)) };
    const fitted = fit(overlay.fromMs ?? 0, overlay.toMs);
    return { ...overlay, fromMs: overlay.fromMs === null ? null : fitted.fromMs, toMs: fitted.toMs };
  }).filter((overlay) => overlay.toMs === null || overlay.fromMs === null || overlay.toMs > overlay.fromMs);
  const texts = list.texts.map((text) => ({ ...text, ...fit(text.fromMs, text.toMs) })).filter((text) => text.toMs > text.fromMs);
  const voiceovers = list.voiceovers.map((voiceover) => ({ ...voiceover, atMs: Math.min(voiceover.atMs, Math.max(0, total - 1)) }));
  const music = list.music ? { ...list.music, startMs: Math.min(list.music.startMs, Math.max(0, total - 1)) } : null;
  return { ...list, overlays, texts, voiceovers, music };
}

/** Changes one segment's speed; what was placed past the new end slides back in. */
export function setSegmentSpeed(list: EditList, segmentId: string, speed: number): EditList {
  const segments = explicitSegments(list).map((segment) => (segment.id === segmentId ? { ...segment, speed } : segment));
  return fitPlaced({ ...list, segments });
}

export function setSegmentBounds(list: EditList, segmentId: string, inMs: number, outMs: number): EditList {
  const segments = explicitSegments(list).map((segment) => (segment.id === segmentId ? { ...segment, inMs: Math.round(inMs), outMs: Math.round(outMs) } : segment));
  if (segments.some((s) => s.outMs - s.inMs < MIN_SEGMENT_MS || s.inMs < 0 || s.outMs > list.sourceDurationMs)) return list;
  return fitPlaced({ ...list, segments });
}

export function removeSegment(list: EditList, segmentId: string): EditList {
  const placements = segmentPlacements(list);
  const hit = placements.find((p) => p.segment.id === segmentId);
  if (!hit || placements.length === 1) return list;
  return removeRange(list, hit.startMs, hit.endMs);
}

/** Changes one segment's mute, hold or speed; what was placed past the new end slides back in. */
export function updateSegment(list: EditList, segmentId: string, change: Partial<Pick<Segment, 'muted' | 'holdMs' | 'speed'>>): EditList {
  const segments = explicitSegments(list).map((segment) => (segment.id === segmentId ? { ...segment, ...change } : segment));
  return fitPlaced({ ...list, segments });
}

/**
 * Drags one end of a kept stretch to a new source time (the timeline's trim handles):
 * kept inside the source and at least a frame long; the other end stays put.
 */
export function trimSegmentEdge(list: EditList, segmentId: string, edge: 'in' | 'out', sourceMs: number): EditList {
  const segment = explicitSegments(list).find((candidate) => candidate.id === segmentId)
    || (list.segments.length === 0 ? explicitSegments(list)[0] : undefined);
  if (!segment) return list;
  const rounded = Math.round(sourceMs);
  const inMs = edge === 'in' ? Math.max(0, Math.min(rounded, segment.outMs - MIN_SEGMENT_MS)) : segment.inMs;
  const outMs = edge === 'out' ? Math.min(list.sourceDurationMs, Math.max(rounded, segment.inMs + MIN_SEGMENT_MS)) : segment.outMs;
  const segments = (list.segments.length === 0 ? [segment] : list.segments).map((candidate) => (candidate.id === segment.id ? { ...candidate, inMs, outMs } : candidate));
  return fitPlaced({ ...list, segments });
}

/** Moves a kept stretch to a new place in the order (0 = first). */
export function moveSegmentTo(list: EditList, segmentId: string, toIndex: number): EditList {
  const segments = explicitSegments(list);
  const index = segments.findIndex((segment) => segment.id === segmentId);
  if (index < 0) return list;
  const target = Math.max(0, Math.min(segments.length - 1, Math.round(toIndex)));
  if (target === index) return list;
  const reordered = segments.slice();
  const [moved] = reordered.splice(index, 1);
  reordered.splice(target, 0, moved);
  return { ...list, segments: reordered };
}

/** Which kept stretch plays at an output time (the last includes its end). */
export function segmentIdAt(list: EditList, outputMs: number): string | null {
  const placements = segmentPlacements(list);
  for (let i = 0; i < placements.length; i += 1) {
    const { startMs, endMs } = placements[i];
    if (outputMs >= startMs && (outputMs < endMs || (i === placements.length - 1 && outputMs <= endMs))) return placements[i].segment.id;
  }
  return null;
}

/** Back to the untouched video: the whole source, nothing placed on it. */
export const resetToOriginal = (list: EditList): EditList => emptyEditList(list.source, list.sourceDurationMs);

// ---------------------------------------------------------------------------
// Talking-head clean-up, from the transcript's word times
// ---------------------------------------------------------------------------

export interface TranscriptWord {
  text: string;
  /** Source seconds. */
  start: number;
  end: number;
}

/** A stretch of the result to remove, in output ms. */
export interface OutputRange {
  fromMs: number;
  toMs: number;
}

/** Words people say when they think, not when they speak; never real words like "like" or "so". */
export const FILLER_WORDS = ['um', 'umm', 'uh', 'uhh', 'uhm', 'erm', 'er', 'ah', 'ahh', 'hmm', 'mm', 'mmm'];

const bare = (word: string): string => word.toLowerCase().replace(/[^a-z]/g, '');

/** Words placed on the result, in output order, with their output times (cut words dropped). */
function placedWords(list: EditList, words: TranscriptWord[]): Array<{ text: string; startMs: number; endMs: number; segmentIndex: number }> {
  const out: Array<{ text: string; startMs: number; endMs: number; segmentIndex: number }> = [];
  segmentPlacements(list).forEach(({ segment, startMs }, segmentIndex) => {
    words.forEach((word) => {
      const ws = word.start * 1000;
      const we = word.end * 1000;
      const middle = (ws + we) / 2;
      if (middle < segment.inMs || middle >= segment.outMs) return;
      const from = Math.max(segment.inMs, ws);
      const to = Math.min(segment.outMs, Math.max(we, from + 1));
      out.push({ text: word.text, startMs: Math.round(startMs + (from - segment.inMs) / segment.speed), endMs: Math.round(startMs + (to - segment.inMs) / segment.speed), segmentIndex });
    });
  });
  return out.sort((a, b) => a.startMs - b.startMs);
}

/**
 * Silences between words longer than `minGapMs`, inside one kept stretch: each is
 * cut down to `keepMs` (half kept on each side), so speech flows without the jumps
 * sounding clipped.
 */
export function pauseRanges(list: EditList, words: TranscriptWord[], minGapMs = 800, keepMs = 300): OutputRange[] {
  const placed = placedWords(list, words);
  const ranges: OutputRange[] = [];
  for (let i = 1; i < placed.length; i += 1) {
    const previous = placed[i - 1];
    const next = placed[i];
    if (previous.segmentIndex !== next.segmentIndex) continue;
    const gap = next.startMs - previous.endMs;
    if (gap < minGapMs) continue;
    const half = Math.round(keepMs / 2);
    const fromMs = previous.endMs + half;
    const toMs = next.startMs - half;
    if (toMs - fromMs >= MIN_SEGMENT_MS) ranges.push({ fromMs, toMs });
  }
  return ranges;
}

/** Filler words ("um", "uh") on the result, each with a few ms around it. */
export function fillerRanges(list: EditList, words: TranscriptWord[], padMs = 40): OutputRange[] {
  const total = outputDurationMs(list);
  return placedWords(list, words)
    .filter((word) => FILLER_WORDS.indexOf(bare(word.text)) >= 0)
    .map((word) => ({ fromMs: Math.max(0, word.startMs - padMs), toMs: Math.min(total, word.endMs + padMs) }))
    .filter((range) => range.toMs - range.fromMs >= MIN_SEGMENT_MS);
}

/** Removes several output ranges at once (from the last, so earlier times don't move). */
export function removeRanges(list: EditList, ranges: OutputRange[]): EditList {
  const merged = ranges
    .slice()
    .sort((a, b) => a.fromMs - b.fromMs)
    .reduce<OutputRange[]>((acc, range) => {
      const last = acc[acc.length - 1];
      if (last && range.fromMs <= last.toMs) last.toMs = Math.max(last.toMs, range.toMs);
      else acc.push({ ...range });
      return acc;
    }, []);
  let next = list;
  for (let i = merged.length - 1; i >= 0; i -= 1) next = removeRange(next, merged[i].fromMs, merged[i].toMs);
  return next;
}

export const rangesTotalMs = (ranges: OutputRange[]): number => ranges.reduce((sum, range) => sum + (range.toMs - range.fromMs), 0);

/** The first few words said in a stretch of the source (for the timeline's clips). */
export function firstWordsIn(words: TranscriptWord[], inMs: number, outMs: number, count = 6): string {
  const said = words.filter((word) => word.start * 1000 >= inMs && word.start * 1000 < outMs).slice(0, count).map((word) => word.text);
  return said.join(' ');
}

// ---------------------------------------------------------------------------
// Text presets
// ---------------------------------------------------------------------------

export type TextPresetId = 'cleanTitle' | 'boldBar' | 'brandLowerThird' | 'subtitleLine';

export interface TextPreset {
  id: TextPresetId;
  label: string;
  apply: (text: TextOverlay, brandColor: string) => TextOverlay;
}

/** Brand colour with an alpha for boxes behind text. */
const withAlpha = (hex: string, alpha: string): string => `${hex.slice(0, 7).toUpperCase()}${alpha}`;

export const TEXT_PRESETS: TextPreset[] = [
  { id: 'cleanTitle', label: 'Clean title', apply: (text) => ({ ...text, font: 'sans', color: '#FFFFFF', backgroundColor: null, effect: 'shadow' as TextEffect, entrance: 'pop' as TextEntrance, fadeMs: 300, sizePct: Math.max(text.sizePct, 8) }) },
  { id: 'boldBar', label: 'Social caption bar', apply: (text) => ({ ...text, font: 'sans', color: '#FFFFFF', backgroundColor: '#000000CC', effect: 'none' as TextEffect, entrance: 'slide' as TextEntrance, fadeMs: 250 }) },
  { id: 'brandLowerThird', label: 'Lower third in your colour', apply: (text, brand) => ({ ...text, font: 'sans', color: '#FFFFFF', backgroundColor: withAlpha(brand, 'E6'), effect: 'none' as TextEffect, entrance: 'slide' as TextEntrance, fadeMs: 300 }) },
  { id: 'subtitleLine', label: 'Plain subtitle', apply: (text) => ({ ...text, font: 'sans', color: '#FFFFFF', backgroundColor: null, effect: 'outline' as TextEffect, entrance: 'fade' as TextEntrance, fadeMs: 0, sizePct: Math.min(text.sizePct, 5) }) },
];

/** One frame at a frame rate, in ms (the transport's smallest step). */
export const frameMs = (fps: number | null | undefined): number => Math.round(1000 / (fps && fps > 0 ? fps : 30));

export function moveSegment(list: EditList, segmentId: string, direction: -1 | 1): EditList {
  const segments = explicitSegments(list);
  const index = segments.findIndex((segment) => segment.id === segmentId);
  const target = index + direction;
  if (index < 0 || target < 0 || target >= segments.length) return list;
  const reordered = segments.slice();
  reordered[index] = segments[target];
  reordered[target] = segments[index];
  return { ...list, segments: reordered };
}

export const defaultPosition = (anchor: Anchor, marginPct = 3): Position => ({ anchor, marginPct, xPct: null, yPct: null });

export function addOverlay(list: EditList, assetId: string, position: Position = defaultPosition('top-right')): EditList {
  if (list.overlays.length >= EDIT_LIMITS.maxOverlays) return list;
  const overlay: Overlay = { id: newItemId('pic'), assetId, position, widthPct: 15, opacity: 1, fromMs: null, toMs: null };
  return { ...list, overlays: [...list.overlays, overlay] };
}

export function addText(list: EditList, style: TextStyle, text: string, atMs: number, options: { secondaryText?: string | null; color?: string; backgroundColor?: string | null; durationMs?: number } = {}): EditList {
  if (list.texts.length >= EDIT_LIMITS.maxTexts) return list;
  const total = outputDurationMs(list);
  const fromMs = Math.max(0, Math.min(Math.round(atMs), Math.max(0, total - 1000)));
  const toMs = Math.min(total, fromMs + (options.durationMs ?? (style === 'title' ? 3000 : 5000)));
  const defaults: Record<TextStyle, { position: Position; sizePct: number }> = {
    title: { position: defaultPosition('center', 0), sizePct: 8 },
    lowerThird: { position: defaultPosition('bottom-left', 8), sizePct: 5 },
    plain: { position: defaultPosition('bottom', 8), sizePct: 5 },
  };
  const item: TextOverlay = {
    id: newItemId('txt'),
    text,
    secondaryText: options.secondaryText || null,
    style,
    font: 'sans',
    position: defaults[style].position,
    sizePct: defaults[style].sizePct,
    color: options.color || '#FFFFFF',
    backgroundColor: options.backgroundColor !== undefined ? options.backgroundColor : style === 'lowerThird' ? '#000000A0' : null,
    effect: 'outline',
    entrance: style === 'title' ? 'pop' : 'fade',
    fadeMs: style === 'plain' ? 0 : 300,
    fromMs,
    toMs,
  };
  return { ...list, texts: [...list.texts, item] };
}

export function addVoiceover(list: EditList, assetId: string, atMs: number): EditList {
  if (list.voiceovers.length >= EDIT_LIMITS.maxVoiceovers) return list;
  const item: Voiceover = { id: newItemId('vo'), source: { kind: 'asset', assetId }, atMs: Math.max(0, Math.round(atMs)), gainDb: 0, duckOriginal: true };
  return { ...list, voiceovers: [...list.voiceovers, item] };
}

export function updateItem<T extends { id: string }>(items: T[], id: string, change: Partial<T>): T[] {
  return items.map((item) => (item.id === id ? { ...item, ...change } : item));
}

export function removeItem<T extends { id: string }>(items: T[], id: string): T[] {
  return items.filter((item) => item.id !== id);
}

// ---------------------------------------------------------------------------
// Undo
// ---------------------------------------------------------------------------

export interface History {
  past: EditList[];
  present: EditList;
  future: EditList[];
  /** The key of the change that made the present: a drag or a slider keeps one key, so it's one undo step. */
  lastKey?: string | null;
}

const MAX_HISTORY = 100;

export const historyOf = (list: EditList): History => ({ past: [], present: list, future: [], lastKey: null });

/**
 * Makes `next` the present. Changes with the same `key` as the one before (a drag,
 * a slider being moved, typing in a field) replace the present instead of adding
 * undo steps, so undo takes back the whole gesture at once.
 */
export function commit(history: History, next: EditList, key?: string | null): History {
  if (next === history.present) return history;
  if (key && history.lastKey === key) return { ...history, present: next, future: [] };
  return { past: [...history.past.slice(-MAX_HISTORY + 1), history.present], present: next, future: [], lastKey: key || null };
}

export function undo(history: History): History {
  if (history.past.length === 0) return history;
  const previous = history.past[history.past.length - 1];
  return { past: history.past.slice(0, -1), present: previous, future: [history.present, ...history.future], lastKey: null };
}

export function redo(history: History): History {
  if (history.future.length === 0) return history;
  const [next, ...rest] = history.future;
  return { past: [...history.past, history.present], present: next, future: rest, lastKey: null };
}

/** Replaces the present after a save brought back the server's canonical copy; history stays. */
export const replacePresent = (history: History, list: EditList): History => ({ ...history, present: list });

// ---------------------------------------------------------------------------
// Words and numbers for the screen
// ---------------------------------------------------------------------------

export const formatMs = (ms: number): string => {
  const total = Math.max(0, Math.round(ms));
  const tenths = Math.floor((total % 1000) / 100);
  const seconds = Math.floor(total / 1000) % 60;
  const minutes = Math.floor(total / 60000) % 60;
  const hours = Math.floor(total / 3600000);
  const mmss = `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}.${tenths}`;
  return hours > 0 ? `${hours}:${mmss}` : mmss;
};

export const formatMinutes = (minutes: number): string => {
  if (minutes >= 60) return `${Math.round(minutes / 6) / 10} h`;
  return `${Math.round(minutes * 10) / 10} min`;
};

/** The source time the preview should show for an output time, clamped into the result. */
export const previewSourceMs = (list: EditList, outputMs: number): number => {
  const total = outputDurationMs(list);
  const clamped = Math.max(0, Math.min(total, outputMs));
  const sourceMs = sourceTimeAt(list, clamped);
  return sourceMs ?? playedSegments(list)[0].inMs;
};

export const previewOutputMs = (list: EditList, sourceMs: number): number | null => outputTimeAt(list, sourceMs);

export const ANCHOR_LABELS: Record<Anchor, string> = {
  'top-left': 'Top left', top: 'Top', 'top-right': 'Top right',
  left: 'Left', center: 'Centre', right: 'Right',
  'bottom-left': 'Bottom left', bottom: 'Bottom', 'bottom-right': 'Bottom right',
};

export const RESOLUTION_LABELS: Record<string, string> = { '720p': '720p', '1080p': '1080p (Full HD)', '2160p': '4K' };
export const ASPECT_LABELS: Record<string, string> = {
  source: 'As recorded',
  '16:9': '16:9 · YouTube',
  '9:16': '9:16 · Shorts, Reels, TikTok',
  '1:1': '1:1 · Square feed',
  '4:5': '4:5 · Feed portrait',
};
/** Short names for the top bar's shape chips. */
export const ASPECT_SHORT: Record<string, string> = { source: 'Original', '16:9': '16:9', '9:16': '9:16', '1:1': '1:1', '4:5': '4:5' };

/** Saving before an export, a send or a copy: the version saved, or why it couldn't be. */
export type SaveFirstResult = { ok: true; version: number } | { ok: false; conflict: boolean; message: string };
